"use client";

import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-16 animate-pulse">
        <div className="h-12 bg-muted w-3/4 mx-auto mb-6"></div>
        <div className="h-4 bg-muted w-1/4 mx-auto mb-12"></div>
        <div className="w-full aspect-video bg-muted mb-12"></div>
        <div className="space-y-4">
          <div className="h-4 bg-muted w-full"></div>
          <div className="h-4 bg-muted w-full"></div>
          <div className="h-4 bg-muted w-5/6"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center text-destructive">
        <p className="font-default">Si è verificato un errore durante il caricamento della guida.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center text-muted-foreground">
        <p className="font-default">Articolo non trovato.</p>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="max-w-4xl mx-auto px-4 md:px-8 py-16">
      <header className="mb-12 text-center">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-default mb-6 text-foreground leading-tight">
          {post.title}
        </h1>
        <div className="text-muted-foreground text-sm font-default flex items-center justify-center gap-3 uppercase tracking-widest">
          {post.date && (
            <time dateTime={post.date}>
              {new Date(post.date).toLocaleDateString("it-IT", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </time>
          )}
          {post.date && post.author && <span>|</span>}
          {post.author && <span>Redazione {post.author.name}</span>}
        </div>
      </header>

      {post.featuredImage && (
        <div className="mb-16">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full aspect-video object-cover rounded-md shadow-lg"
          />
        </div>
      )}

      <div
        className="font-default text-foreground text-lg leading-relaxed max-w-3xl mx-auto 
                   [&>p]:mb-6 [&>h2]:text-3xl [&>h2]:font-bold [&>h2]:font-default [&>h2]:mt-12 [&>h2]:mb-6 [&>h2]:text-primary
                   [&>h3]:text-2xl [&>h3]:font-bold [&>h3]:font-default [&>h3]:mt-8 [&>h3]:mb-4 
                   [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6
                   [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-6 [&>blockquote]:italic [&>blockquote]:my-8 [&>blockquote]:bg-muted/30 [&>blockquote]:py-4"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      {post.author && (
        <div className="mt-20 p-8 border border-border bg-card flex flex-col sm:flex-row items-center sm:items-start gap-6 rounded-lg shadow-sm">
          <Avatar className="size-24 rounded-full border-2 border-primary/20">
            <AvatarImage src={post.author.avatar?.["96"]} alt={post.author.name} />
            <AvatarFallback className="rounded-full font-default text-2xl bg-primary text-primary-foreground">
              {post.author.name.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div className="text-center sm:text-left flex-1">
            <h3 className="text-xl font-bold font-default mb-2 text-foreground">
              Analisi a cura di {post.author.name}
            </h3>
            <p className="text-muted-foreground font-default leading-relaxed">
              Esperto in turismo medicale e consulente per la mobilità sanitaria internazionale. Si occupa di analizzare gli standard qualitativi delle cliniche dentali nei Balcani per garantire trasparenza ai pazienti italiani.
            </p>
          </div>
        </div>
      )}
    </article>
  );
}

function RelatedPostsContent() {
  const { posts, loading, error } = usePosts();

  if (loading || error || !posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-20">
      <h2 className="text-3xl font-bold font-default mb-12 text-center text-foreground">
        Approfondimenti Correlati
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {posts.map((post, i) => (
          <Card
            key={post.id}
            data-index={i}
            className="rounded-lg shadow-sm border border-border flex flex-col bg-card hover:shadow-md transition-all duration-300"
          >
            {post.featuredImage && (
              <div className="aspect-[4/3] w-full overflow-hidden rounded-t-lg">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                />
              </div>
            )}
            <CardHeader className="flex-grow pt-6 px-6 pb-4">
              <CardTitle className="font-default text-xl font-bold leading-snug line-clamp-3 text-foreground hover:text-primary transition-colors">
                {post.title}
              </CardTitle>
            </CardHeader>
            <CardFooter className="px-6 pb-6 pt-0">
              <Link to={post.link || "#"} className="w-full">
                <Button
                  variant="outline"
                  className="w-full rounded-md border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors font-default font-semibold uppercase tracking-wide text-xs py-6"
                >
                  Leggi la guida
                </Button>
              </Link>
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && window.wvcClient ? window.wvcClient.getCurrentWPQueryParams() : { post_type: "post", per_page: 1 };
  const wp_query = new WP_Query(currentWPQueryParams);

  
  const related_query_params = { post_type: "post", per_page: 3 };
  const related_wp_query = new WP_Query(related_query_params);

  return (
    <section data-section-id="234733" id="single-post-template-section" className="bg-background text-foreground min-h-screen w-full">
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
      </PostsProvider>
      
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <Separator className="bg-border/50" />
      </div>

      <PostsProvider wp_query={{ post_type: "post", per_page: 3 }}>
        <RelatedPostsContent />
      </PostsProvider>
    </section>
  );
}